import React from "react";

/**
 * Props for icons using the size prop wrapper
 * Supports both size prop and original height/width props for backward compatibility
 */
export interface IconWithSizeProps extends React.SVGProps<SVGSVGElement> {
  size?: number | string;
  // SVGR injects these accessibility props; including them here ensures
  // ComponentType<IconWithSizeProps> accepts SVGR-generated components without any cast
  title?: string;
  titleId?: string;
  desc?: string;
  descId?: string;
}

// Forward-ref + sync render shape for SVG icons
// Satisfies a plain sync callable FC (used in react < 19)
// so consumers typed as FC<P> are happy:
type SyncIconWithRef = React.ForwardRefExoticComponent<
  IconWithSizeProps & React.RefAttributes<SVGSVGElement>
>;

/**
 * Higher-order component that wraps SVGR-generated icons to accept a single `size` prop
 * instead of separate `height` and `width` props.
 *
 * @param IconComponent - The SVGR-generated React component
 * @returns A wrapped component that accepts a `size` prop
 *
 * @example
 * ```tsx
 * // Original SVGR import
 * import EditIconOriginal from "../assets/edit.svg?react";
 *
 * // Wrapped with size prop
 * export const EditIcon = withSize(EditIconOriginal);
 *
 * // Usage - all equivalent:
 * <EditIcon size={24} />
 * <EditIcon height={24} width={24} />
 * <EditIcon />  // defaults to 14px
 * ```
 */
export function withSize(IconComponent: React.ComponentType<IconWithSizeProps>): SyncIconWithRef {
  const WrappedIcon = React.forwardRef<SVGSVGElement, IconWithSizeProps>(
    ({ size, height, width, ...props }, ref) => {
      // If size is provided, use it for both height and width
      // Otherwise, fall back to individual height/width props (with 14 as default)
      const iconHeight = size ?? height ?? 14;
      const iconWidth = size ?? width ?? 14;

      return <IconComponent height={iconHeight} ref={ref} width={iconWidth} {...props} />;
    },
  );

  WrappedIcon.displayName = `withSize(${IconComponent.displayName || IconComponent.name || "Icon"})`;
  return WrappedIcon;
}
